/* istanbul ignore file */
// TODO [LEARN-15811]: ^ ignore coverage on this file until we can have app test scaffolding ready
import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router'
import { ErrorMessage } from 'secondstep-components'
import { resolveCourseCode, validateClassId } from 'pages/UnitV3/data'
import { getClassId, parseGradeAndProgram } from 'utils/lmsHelpers'
import {
  getProductNameFromUrl,
  resolveRouteServiceApp,
  isTier2PreviewMode,
  isDigitalWellBeingPreviewMode,
  isDigitalWellBeingUrl,
  isHighSchoolUrl,
  isK5Url,
  isMiddleSchoolUrl,
} from 'utils/routing/routeHelpers'
import { CurriculumRouteContentGetter as RouteContentGetter } from 'layers/content/v3/Hocs/CurriculumRouteContentGetter'
import withSiteContext from 'layers/content/v2/Hocs/withSiteContext'
import { navigationEvent } from 'layers/navigation/store/operations'
import withApiError from 'layers/errorHandling/apiError'
import { userSegmentationBuilder } from 'utils/userSegmentationHelpers'
import {
  AI_HS_PAGE_USAGE_KEY,
  sendApplicationInsightsEvent,
} from 'utils/sendAnalyticsDataHelpers'
import { isPreviewMode } from 'utils/highschoolHelpers'
import {
  operations as lmsOperations,
  selectors as lmsSelectors,
} from 'store/lmsManager'
import { selectors as contextSelectors } from 'auth/stores/userContext'

import Component from './component'

const IndividualResourceContainer = props => {
  const {
    currentSite,
    routeContent,
    userSegmentationInfo,
    navigationEvent,
    productTabTitle,
    instructorData,
    hasFetchedInstructorProgramFlex,
    fetchInstructorProgramFlexGet,
    userContext,
    match,
    location,
  } = props || {}
  const { content = {}, loading } = routeContent
  const queryType = routeContent?.route?.queryType
  const learningExperience =
    (queryType && content?.[queryType]) || content?.learningExperience || null

  const isTeachRoute = match?.params?.verb === 'teach'
  // Treat the instructor data as still loading only if:
  // - the current route is a teach route, AND
  // - the fetch hasn't completed yet (hasFetchedInstructorProgramFlex is false), AND
  // - we have no cached instructor data to render with.
  // This prevents getting stuck when LMS succeeds but returns no key for this
  // program type (e.g. no Dwb key) — once hasFetchedInstructorProgramFlex flips true
  // the page unblocks and can render (showing a validation error if the class is invalid).
  const isInstructorDataLoading =
    isTeachRoute &&
    !hasFetchedInstructorProgramFlex &&
    (!instructorData || Object.keys(instructorData).length === 0)

  const readyToLoad =
    !loading && !!learningExperience && !isInstructorDataLoading

  useEffect(() => {
    sendApplicationInsightsEvent(AI_HS_PAGE_USAGE_KEY, userSegmentationInfo)
  }, [content])

  const isRoutePreview =
    String(match?.params?.verb || '').toLowerCase() === 'preview'
  const isTier2Preview = isTier2PreviewMode()
  const isDwbPreview = isDigitalWellBeingPreviewMode()
  const isHsPreview = isPreviewMode() && isHighSchoolUrl()
  // Treat explicit preview routes and helper-detected preview modes consistently.
  const isPreview =
    isRoutePreview || isTier2Preview || isDwbPreview || isHsPreview

  // When the LMS player is closed and focus returns to this tab, refresh
  // instructor/me so Mark Done and Teach/Resume/Review update without reload.
  const wasHiddenRef = useRef(false)
  useEffect(() => {
    if (isPreview || !isTeachRoute) return undefined
    if (typeof fetchInstructorProgramFlexGet !== 'function') return undefined

    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        wasHiddenRef.current = true
        return
      }

      if (document.visibilityState === 'visible' && wasHiddenRef.current) {
        wasHiddenRef.current = false
        fetchInstructorProgramFlexGet()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [fetchInstructorProgramFlexGet, isPreview, isTeachRoute])

  if (!readyToLoad) {
    return null
  }

  if (isTeachRoute) {
    const classId = getClassId(location)
    // Tier-2: parseGradeAndProgram maps grades-6-8 → 6-8_curriculum_tier2 (same as TieredInterventions).
    const gradeSegment = match?.url?.includes('tier-2')
      ? parseGradeAndProgram(match.url)?.grade
      : match?.params?.grade
    const courseCode = resolveCourseCode({
      coursePointer: routeContent?.coursePointer,
      unit: learningExperience,
      gradeSegment,
      contentGroupCode: routeContent?.contentGroupCode,
    })

    const validation = validateClassId({
      instructorData,
      classId,
      siteId: currentSite?.siteId,
      courseCode,
      gradeSegment,
    })

    if (!validation.isValid) {
      const productName =
        getProductNameFromUrl(location?.pathname) || 'digital-well-being'
      return (
        <ErrorMessage
          additionalDetails={`Please create and select a valid class from the [/product/${productName}/lessons](/product/${productName}/lessons) page.`}
          statusCode={400}
        />
      )
    }
  }

  return (
    <Component
      coursePointer={routeContent?.coursePointer || null}
      entry={learningExperience}
      gradeInstructorData={instructorData}
      isHsPreview={isHsPreview}
      isPreview={isPreview}
      match={match}
      navigationEvent={navigationEvent}
      productTabTitle={productTabTitle}
      routeContent={routeContent}
      siteId={currentSite?.siteId ?? null}
      userContext={userContext}
    />
  )
}

IndividualResourceContainer.propTypes = {
  currentSite: PropTypes.shape({
    siteId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  fetchInstructorProgramFlexGet: PropTypes.func,
  hasFetchedInstructorProgramFlex: PropTypes.bool,
  instructorData: PropTypes.object,
  location: PropTypes.object,
  routeContent: PropTypes.shape({
    content: PropTypes.shape({
      learningExperience: PropTypes.object,
    }),
    coursePointer: PropTypes.object,
    loading: PropTypes.bool,
  }),
  userContext: PropTypes.object,
  userSegmentationInfo: PropTypes.object,
}

const mapStateToProps = (state, _) => {
  // Determine which instructor data to use based on the current URL
  let instructorData = null
  if (isDigitalWellBeingUrl()) {
    instructorData = lmsSelectors.selectDwbInstructorData(state)
  } else if (isHighSchoolUrl()) {
    instructorData = lmsSelectors.selectHsInstructorData(state)
  } else if (isMiddleSchoolUrl()) {
    instructorData = lmsSelectors.selectMiddleSchoolInstructorData(state)
  } else if (isK5Url()) {
    instructorData = lmsSelectors.selectk5InstructorData(state)
  }

  return {
    userSegmentationInfo: userSegmentationBuilder(state),
    productTabTitle: state.productManager?.productMap?.name || '',
    instructorData,
    hasFetchedInstructorProgramFlex:
      state.lmsManager?.hasFetchedInstructorProgramFlex ?? false,
    userContext: contextSelectors.selectProgramContext(state),
  }
}

const mapDispatchToProps = {
  navigationEvent,
  fetchInstructorProgramFlexGet: lmsOperations.fetchInstructorProgramFlexGet,
}

const options = {
  app: ({ route }) => resolveRouteServiceApp(route),
}

const HydratedComponent = withRouter(
  connect(mapStateToProps, mapDispatchToProps)(IndividualResourceContainer),
)

export default withSiteContext(
  RouteContentGetter(withApiError(HydratedComponent), options),
)
